import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  useBreakpointValue
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { CheckIcon } from '@chakra-ui/icons';

const MotionBox = motion(Box);

/**
 * Course Features Section
 * Key features and included materials
 */
const CourseFeatures = ({ data }) => {
  const gridCols = useBreakpointValue({ base: 1, md: 2 });

  if (!data) return null;

  const {
    title = "Why Choose This Course?",
    subtitle,
    features = []
  } = data;

  if (features.length === 0) return null;

  return (
    <Box py={{ base: 16, md: 20 }} bg="gray.50" borderY="1px solid" borderColor="gray.200">
      <Container maxW="container.lg">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          mb={{ base: 12, md: 16 }}
        >
          <VStack align="center" spacing={3} textAlign="center">
            <Heading as="h2" size="2xl" fontWeight="900" color="gray.900">
              {title}
            </Heading>
            {subtitle && (
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                {subtitle}
              </Text>
            )}
          </VStack>
        </MotionBox>

        {/* Features Grid */}
        <SimpleGrid columns={gridCols} spacing={{ base: 8, md: 10 }}>
          {features.map((feature, idx) => (
            <MotionBox
              key={idx}
              initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.6 }}
            >
              <HStack align="flex-start" spacing={4}>
                <Box
                  flexShrink={0}
                  w={12}
                  h={12}
                  bg="brand.100"
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {feature.icon ? (
                    <Box fontSize="xl">{feature.icon}</Box>
                  ) : (
                    <Icon as={CheckIcon} color="brand.600" boxSize={6} />
                  )}
                </Box>
                <VStack align="start" spacing={1} flex={1}>
                  <Heading as="h3" size="sm" color="gray.900">
                    {feature.title}
                  </Heading>
                  {feature.description && (
                    <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                      {feature.description}
                    </Text>
                  )}
                </VStack>
              </HStack>
            </MotionBox>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default CourseFeatures;
